import { useRef, useState } from "react";
import { Moon, Sun, Loader2, CheckCircle2, AlertTriangle, RotateCcw, Camera, Pencil, Keyboard } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Field, NSelect, Loading, Plate, useFetch } from "@/components/common";
import { PleinCta } from "@/components/Pleins";
import { SecureImg } from "@/components/Photos";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fNum, fDate, todayIso } from "@/lib/api";

const defaultType = () => ([5, 6, 0].includes(new Date().getDay()) ? "vendredi" : "lundi");
const fTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const MANUAL = { camera_indisponible: "Appareil photo indisponible", lecture_echouee: "Lecture de la photo impossible", autorisation_admin: "Autorisation exceptionnelle de l'administrateur" };

function TypeToggle({ value, onChange }) {
  const opts = [["vendredi", "Vendredi soir", Moon], ["lundi", "Lundi matin", Sun]];
  return (
    <div className="grid grid-cols-2 gap-2">
      {opts.map(([v, label, Icon]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`reading-type-${v}`}
          className={`h-16 rounded-lg border-2 flex items-center justify-center gap-2 font-bold transition-[background-color,border-color] ${value === v ? "bg-[#0E2D43] border-[#0E2D43] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
          <Icon className={`w-5 h-5 ${value === v ? "text-[#F49619]" : ""}`} />{label}
        </button>
      ))}
    </div>
  );
}

function Done({ done, onNew }) {
  const r = done.reading;
  return (
    <div className="max-w-lg mx-auto text-center py-12 rise" data-testid="reading-success">
      <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
      <h2 className="font-display text-2xl font-bold text-[#0E2D43] mt-4">Relevé enregistré</h2>
      <p className="text-slate-500 mt-2">{r.type === "vendredi" ? "Vendredi soir" : "Lundi matin"} · {fDate(r.date)} · <b data-testid="reading-success-km">{fNum(r.mileage)} km</b></p>
      <p className="text-xs text-slate-400 mt-1" data-testid="reading-success-source">{r.source === "photo" ? (r.corrected ? "Photo — valeur corrigée manuellement" : "Photo — lecture confirmée") : "Saisie manuelle"}</p>
      {done.perso_km !== null && (
        <div className="mt-6 rounded-xl bg-[#0E2D43] text-white p-5" data-testid="reading-perso-km">
          <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold">Km personnels du week-end</div>
          <div className="font-display text-4xl font-extrabold text-[#F49619] mt-1">{fNum(done.perso_km)} km</div>
        </div>
      )}
      {done.warnings.map((w) => <div key={w} className="mt-3 flex items-center gap-2 text-left text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3" data-testid="reading-warning"><AlertTriangle className="w-4 h-4 shrink-0" />{w}</div>)}
      <button onClick={onNew} className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-md bg-[#0E2D43] text-white font-semibold" data-testid="reading-new-button"><RotateCcw className="w-4 h-4" /> Nouveau relevé</button>
    </div>
  );
}

function OcrResult({ ocr, onConfirm, onRetake, onManual, busy }) {
  const [edit, setEdit] = useState(false);
  const [km, setKm] = useState(ocr.detected ? String(ocr.detected) : "");
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-4 rise" data-testid="ocr-result">
      <div className="flex gap-3">
        <SecureImg id={ocr.file_id} className="w-28 h-28 rounded-lg border shrink-0" testid="ocr-photo" />
        <div className="text-xs text-slate-500" data-testid="ocr-captured-at">Photo prise le {fDate(ocr.captured_at.slice(0, 10))} à {fTime(ocr.captured_at)}{ocr.last_known ? <div className="mt-1">Dernier kilométrage connu : {fNum(ocr.last_known)} km</div> : null}</div>
      </div>
      {ocr.readable ? (
        <div className="text-center"><div className="text-sm text-slate-500">Kilométrage détecté</div><div className="font-display text-4xl font-extrabold text-[#0E2D43]" data-testid="ocr-detected">{fNum(ocr.detected)} km</div></div>
      ) : (
        <div className="text-center text-rose-700 font-semibold" data-testid="ocr-unreadable">Impossible de lire correctement le kilométrage. Reprendre la photo.</div>
      )}
      {ocr.readable && ocr.warnings.map((w) => <div key={w} className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3" data-testid="ocr-warning"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{w}</div>)}
      {ocr.readable && ocr.warnings.length > 0 && <div className="text-xs text-amber-700 font-semibold" data-testid="ocr-manual-validation">Validation manuelle requise : vérifiez la valeur avant de confirmer.</div>}
      {edit && <Field label="Kilométrage correct"><Input type="number" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} className="h-14 text-2xl font-mono bg-white" autoFocus data-testid="ocr-correct-input" /></Field>}
      {ocr.readable ? (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" disabled={busy || !km} onClick={() => onConfirm(Number(km))} className="h-14 rounded-lg bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold disabled:bg-slate-300" data-testid="ocr-confirm-button">
            {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : edit ? "Valider la correction" : "Confirmer"}
          </button>
          {!edit ? <button type="button" onClick={() => setEdit(true)} className="h-14 rounded-lg border-2 border-[#0E2D43] text-[#0E2D43] font-bold flex items-center justify-center gap-2" data-testid="ocr-correct-button"><Pencil className="w-4 h-4" /> Corriger</button>
            : <button type="button" onClick={onRetake} className="h-14 rounded-lg border-2 border-slate-300 text-slate-600 font-bold" data-testid="ocr-retake-button">Reprendre la photo</button>}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onRetake} className="h-14 rounded-lg bg-[#0E2D43] text-white font-bold flex items-center justify-center gap-2" data-testid="ocr-retake-button"><Camera className="w-4 h-4" /> Reprendre la photo</button>
          <button type="button" onClick={onManual} className="h-14 rounded-lg border-2 border-slate-300 text-slate-600 font-bold" data-testid="ocr-manual-button">Saisie manuelle</button>
        </div>
      )}
    </div>
  );
}

function ManualForm({ admin, allowed, initialReason, onSubmit, onCancel, busy }) {
  const [km, setKm] = useState("");
  const [reason, setReason] = useState(initialReason || "camera_indisponible");
  const opts = Object.entries(MANUAL).filter(([k]) => k !== "autorisation_admin" || allowed).map(([k, l]) => ({ value: k, label: l }));
  return (
    <div className="bg-white border-2 border-amber-300 rounded-xl p-4 sm:p-5 space-y-4" data-testid="manual-form">
      <div className="text-sm font-bold text-amber-800">Saisie manuelle (solution de secours) — sera identifiée comme « Saisie manuelle »</div>
      {!admin && <Field label="Motif"><NSelect value={reason} onChange={setReason} options={opts} testid="manual-reason-select" /></Field>}
      <Field label="Kilométrage compteur"><Input type="number" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} className="h-14 text-2xl font-mono bg-white" data-testid="reading-mileage-input" /></Field>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" disabled={busy || !km} onClick={() => onSubmit(Number(km), reason)} className="h-12 rounded-lg bg-[#F49619] text-[#0E2D43] font-bold disabled:bg-slate-300" data-testid="reading-submit-button">Enregistrer</button>
        <button type="button" onClick={onCancel} className="h-12 rounded-lg border-2 border-slate-300 text-slate-600 font-bold" data-testid="manual-cancel">Annuler</button>
      </div>
    </div>
  );
}

export default function Releve() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const me = useFetch(admin ? null : "/commerciaux/me");
  const coms = useFetch(admin ? "/users" : null, { role: "commercial", with_m2: true });
  const cam = useRef();
  const [uid, setUid] = useState("");
  const [type, setType] = useState(defaultType());
  const [date, setDate] = useState(todayIso());
  const [step, setStep] = useState("start");
  const [ocr, setOcr] = useState(null);
  const [manualReason, setManualReason] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  if (!admin && (me.loading || !me.data)) return <Loading />;
  const selected = admin ? (coms.data || []).find((c) => c.id === uid) : null;
  const vehicle = admin ? selected?.vehicles?.find((v) => v.type === "commercial") : me.data.vehicle;
  const last = admin ? null : me.data.last_reading;

  const analyse = async (file) => {
    if (!file) return;
    setStep("analyse");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", type);
    fd.append("captured_at", new Date().toISOString());
    if (admin) fd.append("user_id", uid);
    try {
      const { data } = await api.post("/readings/ocr", fd);
      setOcr(data);
      setStep("result");
    } catch (e) {
      toast.error(errMsg(e));
      setStep("start");
    }
  };
  const save = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post("/readings", { user_id: admin ? uid : undefined, type, date: admin ? date : todayIso(), ...body });
      setDone(data);
      if (!admin) me.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const reset = () => {
    setDone(null);
    setOcr(null);
    setStep("start");
  };
  const retake = () => {
    setStep("start");
    setTimeout(() => cam.current?.click(), 50);
  };
  if (done) return <Done done={done} onNew={reset} />;

  return (
    <div className="max-w-xl" data-testid="reading-page">
      <PageHeader overline="Véhicules commerciaux" title="Relevé kilométrique" />
      {!admin && <div className="mb-4"><PleinCta /></div>}
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { analyse(e.target.files?.[0]); e.target.value = ""; }} data-testid="odometer-photo-input" />
      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3 rise">
          {admin ? (
            <Field label="Commercial"><NSelect value={uid} onChange={setUid} placeholder="Choisir…" testid="reading-commercial-select" options={(coms.data || []).map((c) => ({ value: c.id, label: c.name }))} /></Field>
          ) : (
            <div><div className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Commercial</div><div className="font-semibold text-[#0E2D43]" data-testid="reading-user-name">{user.name}</div></div>
          )}
          <div>
            {vehicle ? <Plate>{vehicle.plate}</Plate> : admin && !uid ? <span className="text-sm text-slate-400">Choisissez un commercial</span> : <span className="text-sm text-rose-600" data-testid="reading-no-vehicle">Aucun véhicule affecté</span>}
            {vehicle && <span className="ml-2 text-xs text-slate-500">{vehicle.brand} {vehicle.model}</span>}
            {last && <div className="text-xs text-slate-500 mt-1" data-testid="reading-last">Dernier relevé : {fNum(last.mileage)} km le {fDate(last.date)}</div>}
          </div>
          {admin && <Field label="Date"><Input type="date" max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} className="h-11 bg-white" data-testid="reading-date-input" /></Field>}
        </div>
        {step !== "result" && <TypeToggle value={type} onChange={setType} />}
        {step === "start" && (
          <>
            <button type="button" disabled={!vehicle} onClick={() => cam.current.click()} className="w-full h-20 rounded-xl bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] text-lg font-extrabold flex items-center justify-center gap-3 shadow-lg disabled:bg-slate-300" data-testid="odometer-photo-button">
              <Camera className="w-7 h-7" /> Prendre la photo du compteur
            </button>
            <button type="button" disabled={!vehicle} onClick={() => { setManualReason(admin ? null : "camera_indisponible"); setStep("manual"); }} className="w-full text-sm text-slate-500 underline flex items-center justify-center gap-1" data-testid="manual-entry-link">
              <Keyboard className="w-4 h-4" /> {admin ? "Saisie manuelle (administrateur)" : "Appareil photo indisponible ? Saisie manuelle"}
            </button>
          </>
        )}
        {step === "analyse" && <div className="bg-white border rounded-xl p-8 text-center" data-testid="ocr-analysing"><Loader2 className="w-10 h-10 animate-spin text-[#F49619] mx-auto" /><div className="mt-3 font-semibold text-[#0E2D43]">Analyse de la photo…</div><div className="text-xs text-slate-500">Lecture automatique du compteur</div></div>}
        {step === "result" && ocr && <OcrResult ocr={ocr} busy={busy} onRetake={retake} onManual={() => { setManualReason("lecture_echouee"); setStep("manual"); }} onConfirm={(km) => save({ mileage: km, attempt_id: ocr.attempt_id })} />}
        {step === "manual" && <ManualForm admin={admin} allowed={!!user.manual_reading_allowed} initialReason={manualReason} busy={busy} onCancel={() => setStep("start")} onSubmit={(km, reason) => save({ mileage: km, manual_reason: admin ? undefined : reason })} />}
      </div>
    </div>
  );
}
